import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { from, Observable, of, switchMap } from 'rxjs';
import { environment } from '@src/environments/environment';
import { isPlatformServer } from '@angular/common';
import { STORAGE_CONST } from '@app/constants/storage-constants';

class CacheConfig {
  createdAt: number;
  expiresIn: number;
  cacheData: any;
}

@Injectable({
  providedIn: 'root',
})
export class StorageUtilLibService {
  private storageObj: Storage | null = null;
  public currentCacheVersion: number;

  constructor(
    private storage: Storage,
    @Inject(PLATFORM_ID) private platformId: string,
    private ngZone: NgZone,
  ) {
    this.init();
  }

  init() {
    from(this.createStorage())
      .pipe()
      .subscribe({
        next: (value: Storage) => {
          this.storageObj = value;
        },
        error: (err: any) => {},
      });
  }

  createStorage(): Observable<Storage> {
    if (!isPlatformServer(this.platformId)) {
      return from(this.storage.create());
    } else {
      return of(null);
    }
  }

  public async setToken(key: string, value: any) {
    if (this.storageObj) {
      await this.storageObj?.set(key, value);
    } else {
      if (!isPlatformServer(this.platformId)) {
        this.storageObj = await this.storage.create();
        await this.storageObj?.set(key, value);
      }
    }
  }

  public getToken(key: string): Observable<any> {
    if (!this.storageObj) {
      if (!isPlatformServer(this.platformId)) {
        return from(this.storage.create()).pipe(
          switchMap((result: Storage) => {
            this.storageObj = result;
            return from(this.storageObj.get(key));
          }),
        );
      } else {
        return of(null);
      }
    } else {
      return from(this.storageObj.get(key));
    }
  }

  public async set(key: string, value: any, ignoreVersion: boolean) {
    if (!isPlatformServer(this.platformId)) {
      if (!this.storageObj) {
        this.storageObj = await this.storage.create();
      }
      if (ignoreVersion) {
        await this.storageObj?.set(key, value);
      } else {
        const cacheConfig: CacheConfig = new CacheConfig();
        cacheConfig.createdAt = new Date().getTime();
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 2);
        cacheConfig.expiresIn = expiryDate?.getTime();
        cacheConfig.cacheData = value;
        await this.storageObj?.set(
          key + environment?.cacheKey + environment.cacheVersion,
          cacheConfig,
        );
      }
    }
  }

  public async get(key: string, ignoreVersion: boolean): Promise<any> {
    if (!isPlatformServer(this.platformId)) {
      if (!this.storageObj) {
        this.storageObj = await this.storage.create();
      }

      const tempKey = ignoreVersion
        ? key
        : key + environment?.cacheKey + environment.cacheVersion;

      if (ignoreVersion) {
        return await this.storageObj.get(tempKey);
      } else {
        const cacheConfig: CacheConfig = await this.storageObj.get(tempKey);
        if (cacheConfig?.expiresIn) {
          const now = new Date();
          const twoDaysInMs: number = 2 * 24 * 60 * 60 * 1000;
          const timeDiffInMs = now.getTime() - cacheConfig?.expiresIn;
          if (timeDiffInMs >= twoDaysInMs) {
            this.removeForceFully(key, ignoreVersion).then();
          } else {
          }
        }

        return cacheConfig?.cacheData;
      }
    }
  }

  public async removeForceFully(key: string, ignoreVersion: boolean) {
    if (!isPlatformServer(this.platformId)) {
      const keys: string[] = await this.storageObj.keys();
      if (keys?.length > 0) {
        const foundedKey: string = keys?.find((item: string) => {
          return item.indexOf(key) > -1;
        });
        if (foundedKey) {
          await this.storageObj.remove(foundedKey);
        }
      }
    }
  }

  public async remove(key: string, ignoreVersion: boolean) {
    if (!isPlatformServer(this.platformId)) {
      const tempKey = ignoreVersion
        ? key
        : key + environment?.cacheKey + environment.cacheVersion;
      await this.storageObj.remove(tempKey);
    }
  }

  public async removeToken(key: string) {
    if (!isPlatformServer(this.platformId)) {
      await this.storageObj.remove(key);
    }
  }

  public async clear() {
    if (!isPlatformServer(this.platformId)) {
      await this.storageObj?.clear();
    }
  }

  createNumberRange(start: number, end: number, step: number): number[] {
    const arrayLength = Math.floor((end - start) / step) + 1;
    return [...Array(arrayLength).keys()].map((x) => x * step + start);
  }

  public clearOldCache() {
    if (Number(this.currentCacheVersion) > 0) {
      const oldCacheVersions: number[] = this.createNumberRange(
        1,
        this.currentCacheVersion - 1,
        1,
      );
      if (oldCacheVersions?.length > 0) {
        this.clearOldKeys(oldCacheVersions);
      }
    }
    this.set(
      STORAGE_CONST.LAST_CACHE_VERSION,
      Number(this.currentCacheVersion),
      true,
    ).then();
  }

  private clearOldKeys(oldCacheVersions: number[]): void {
    this.ngZone.runOutsideAngular(() => {
      this.storageObj
        .keys()
        .then((keys) =>
          keys
            .filter((k) => {
              for (const oldCacheVersion of oldCacheVersions) {
                if (k.endsWith(environment?.cacheKey + oldCacheVersion)) {
                  return true;
                }
              }
              return false;
            })
            .map((k) => this.storageObj.remove(k)),
        )
        .then((promises) => Promise.all(promises))
        .then((entries) => {});
    });
  }
}
